/**
 * InferenceTasks store — leaf module (Perf 3.1.b Step 5). Cross-store refs via runtime getStores().
 */
import { create } from 'zustand';
import { inferenceTasksApi } from '@/lib/api/inferenceTasks';
import { performNativeTask } from '@/lib/localRuntime/nativeTaskOperations';

const getStores = () => require('@/store');
const workSessionRequests = new Map();

const useInferenceTasksStore = create((set, get) => ({
  // State
  tasks: {},          // Map of tasks by id
  taskIds: [],        // Ordered list of task ids
  isLoading: {
    fetch: false,
    create: false,
    update: false,
    delete: false,
    runNow: false,
  },
  errors: {
    fetch: null,
    create: null,
    update: null,
    delete: null,
    runNow: null,
  },
  pagination: {
    page: 1,
    pages: 1,
    total: 0,
    perPage: 20,
    hasMore: false,
  },

  // Helpers
  addTasks: (newTasks, { includeInList = true } = {}) => {
    if (!Array.isArray(newTasks)) {
      newTasks = [newTasks];
    }

    const tasksMap = {};
    const newIds = [];
    const allModels = [];

    const processedTasks = newTasks.map((task) => {
      if (!task || !task.id) return null;

      // Clone task to avoid mutating the original object if needed, 
      // though here we are creating a new processed object.
      const processed = { ...task };

      // Store modelIds and remove models from the task object
      if (processed.models && Array.isArray(processed.models)) {
        processed.modelIds = processed.models.map(m => m.id);
        allModels.push(...processed.models);
        delete processed.models;
      }

      if (processed.conversation) {
        processed.conversationId = processed?.conversationId || processed?.conversation?.id || processed?.conversation;
        delete processed.conversation;
      }

      if (processed.collection) {
        processed.collectionId = processed?.collectionId || processed?.collection?.id || processed?.collection;
        delete processed.collection;
      }

      if (processed.workSession !== undefined) {
        processed.workSessionId = processed.workSession
          ? String(processed.workSession?.id || processed.workSession)
          : null;
        delete processed.workSession;
      }

      tasksMap[processed.id] = processed;
      newIds.push(processed.id);
      
      return processed;
    });

    // Add any new models to the model store
    if (allModels.length > 0) {
      getStores().useModelStore.getState().addModels(allModels);
    }

    set((state) => ({
      tasks: {
        ...state.tasks,
        ...tasksMap,
      },
      taskIds: includeInList ? [...new Set([...state.taskIds, ...newIds])] : state.taskIds,
    }));

    return processedTasks.filter(Boolean);
  },

  // Actions
  fetchTasks: async (page = 1) => {
    try {
      set((state) => ({
        isLoading: { ...state.isLoading, fetch: true },
        errors: { ...state.errors, fetch: null },
      }));

      const { tasks, pagination } = await inferenceTasksApi.fetchTasks(page);

      // Normalize into map
      get().addTasks(tasks);

      set((state) => ({
        taskIds:
          page === 1
            ? tasks.map((t) => t.id)
            : [...new Set([...state.taskIds, ...tasks.map((t) => t.id)])],
        pagination,
        isLoading: { ...state.isLoading, fetch: false },
      }));

      return tasks;
    } catch (error) {
      set((state) => ({
        errors: {
          ...state.errors,
          fetch: error.message || 'Failed to fetch inference tasks',
        },
        isLoading: { ...state.isLoading, fetch: false },
      }));
      throw error;
    }
  },

  // Tasks bound to one Work chat (Sources panel). Merges into the map without
  // disturbing the /tasks page's paginated id list.
  fetchWorkSessionTasks: async (workSessionId) => {
    if (!workSessionId) return [];
    const key = String(workSessionId);
    if (workSessionRequests.has(key)) return workSessionRequests.get(key);
    const snapshot = get().tasks;
    const request = (async () => {
      const fetched = [];
      let page = 1;
      let hasMore;
      do {
        const { tasks, pagination } = await inferenceTasksApi.fetchTasks(page, { workSessionId: key });
        fetched.push(...tasks);
        hasMore = pagination?.hasMore ?? page < (pagination?.pages || 1);
        page += 1;
      } while (hasMore);

      if (workSessionRequests.get(key) !== request) return [];
      const fetchedIds = new Set(fetched.map((task) => task.id));
      // A refresh must not resurrect a deletion or overwrite an edit made
      // while its pages were in flight.
      const current = get().tasks;
      get().addTasks(fetched.filter((task) => current[task.id] === snapshot[task.id]), { includeInList: false });
      set((state) => {
        const removedIds = new Set(Object.keys(snapshot).filter((id) => (
          String(snapshot[id]?.workSessionId || '') === key
          && !fetchedIds.has(id)
          && state.tasks[id] === snapshot[id]
        )));
        return {
          tasks: Object.fromEntries(Object.entries(state.tasks).filter(([id]) => !removedIds.has(id))),
          taskIds: state.taskIds.filter((id) => !removedIds.has(id)),
        };
      });
      return Object.values(get().tasks).filter((task) => String(task.workSessionId || '') === key);
    })();
    workSessionRequests.set(key, request);
    try {
      return await request;
    } finally {
      if (workSessionRequests.get(key) === request) workSessionRequests.delete(key);
    }
  },

  fetchTask: async (taskId) => {
    const stored = get().tasks[taskId];
    if (stored) return stored;

    const task = await inferenceTasksApi.fetchTask(taskId);
    get().addTasks(task);
    return get().tasks[taskId] || task;
  },

  createTask: async (payload) => {
    try {
      set((state) => ({
        isLoading: { ...state.isLoading, create: true },
        errors: { ...state.errors, create: null },
      }));

      const task = await inferenceTasksApi.createTask(payload);
      get().addTasks(task);

      set((state) => ({
        isLoading: { ...state.isLoading, create: false },
      }));

      return task;
    } catch (error) {
      set((state) => ({
        errors: {
          ...state.errors,
          create: error.message || 'Failed to create inference task',
        },
        isLoading: { ...state.isLoading, create: false },
      }));
      throw error;
    }
  },

  updateTask: async (taskId, updates) => {
    try {
      set((state) => ({
        isLoading: { ...state.isLoading, update: true },
        errors: { ...state.errors, update: null },
      }));

      const current = get().tasks[taskId];
      const task = current?.nativeWork ? await performNativeTask(current, 'update', updates) : await inferenceTasksApi.updateTask(taskId, updates);
      get().addTasks(task, { includeInList: false });

      set((state) => ({
        isLoading: { ...state.isLoading, update: false },
      }));

      return task;
    } catch (error) {
      set((state) => ({
        errors: {
          ...state.errors,
          update: error.message || 'Failed to update inference task',
        },
        isLoading: { ...state.isLoading, update: false },
      }));
      throw error;
    }
  },

  deleteTask: async (taskId) => {
    try {
      set((state) => ({
        isLoading: { ...state.isLoading, delete: true },
        errors: { ...state.errors, delete: null },
      }));

      const current = get().tasks[taskId];
      if (current?.nativeWork) await performNativeTask(current, 'remove');
      else await inferenceTasksApi.deleteTask(taskId);

      getStores().useAuthStore.getState().removeFavoriteTaskId(taskId);

      set((state) => ({
        tasks: Object.fromEntries(
          Object.entries(state.tasks).filter(([id]) => id !== taskId)
        ),
        taskIds: state.taskIds.filter((id) => id !== taskId),
        isLoading: { ...state.isLoading, delete: false },
      }));

      return true;
    } catch (error) {
      set((state) => ({
        errors: {
          ...state.errors,
          delete: error.message || 'Failed to delete inference task',
        },
        isLoading: { ...state.isLoading, delete: false },
      }));
      throw error;
    }
  },

  // Run task immediately
  runTaskNow: async (taskId) => {
    try {
      set((state) => ({
        isLoading: { ...state.isLoading, runNow: true },
        errors: { ...state.errors, runNow: null },
      }));

      const currentTask = get().tasks[taskId];
      const result = currentTask?.nativeWork ? await performNativeTask(currentTask, 'runNow') : await inferenceTasksApi.runTaskNow(taskId);
      const conversationId = result.conversationId || null;
      const sessionId = result.sessionId || result.id || null;

      if (conversationId) {
        const current = get().tasks[taskId];
        if (current) {
          set((state) => ({
            tasks: {
              ...state.tasks,
              [taskId]: { ...current, conversationId },
            },
          }));
        }
        if (sessionId) {
          getStores().useCoworkerStore.getState().addTask({
            id: String(sessionId),
            conversationId: String(conversationId),
            status: 'running',
            title: current?.name || '',
          });
        }
      }

      set((state) => ({
        isLoading: { ...state.isLoading, runNow: false },
      }));

      return { ...result, conversationId, sessionId };
    } catch (error) {
      set((state) => ({
        errors: {
          ...state.errors,
          runNow: error.message || 'Failed to run task',
        },
        isLoading: { ...state.isLoading, runNow: false },
      }));
      throw error;
    }
  },

  // Toggle task enabled/disabled state
  toggleTask: async (taskId, isEnabled) => {
    // Get current task state for potential rollback
    const currentTask = get().tasks[taskId];
    const previousEnabled = currentTask?.flags?.isEnabled;

    try {
      // Optimistically update local state immediately
      set((state) => ({
        isLoading: { ...state.isLoading, update: true },
        errors: { ...state.errors, update: null },
        tasks: {
          ...state.tasks,
          [taskId]: {
            ...state.tasks[taskId],
            flags: {
              ...state.tasks[taskId]?.flags,
              isEnabled,
            },
          },
        },
      }));

      // Send isEnabled at root level (backend expects this format)
      const task = currentTask?.nativeWork ? await performNativeTask(currentTask, 'update', { isEnabled })
        : await inferenceTasksApi.updateTask(taskId, { isEnabled });

      // Update with server response
      if (task) {
        get().addTasks(task, { includeInList: false });
      }

      set((state) => ({
        isLoading: { ...state.isLoading, update: false },
      }));

      return task;
    } catch (error) {
      // Rollback optimistic update on error
      set((state) => ({
        errors: {
          ...state.errors,
          update: error.message || 'Failed to toggle task',
        },
        isLoading: { ...state.isLoading, update: false },
        tasks: {
          ...state.tasks,
          [taskId]: {
            ...state.tasks[taskId],
            flags: {
              ...state.tasks[taskId]?.flags,
              isEnabled: previousEnabled,
            },
          },
        },
      }));
      throw error;
    }
  },

  // Selectors
  getTaskById: (id) => get().tasks[id],

  getAllTasks: () => {
    const state = get();
    return state.taskIds.map((id) => state.tasks[id]).filter(Boolean);
  },

  // Reset
  clearTasks: () => {
    workSessionRequests.clear();
    set({
      tasks: {},
      taskIds: [],
      isLoading: {
        fetch: false,
        create: false,
        update: false,
        delete: false,
      },
      errors: {
        fetch: null,
        create: null,
        update: null,
        delete: null,
      },
      pagination: {
        page: 1,
        pages: 1,
        total: 0,
        perPage: 20,
        hasMore: false,
      },
    });
  },
}));

export { useInferenceTasksStore };
export default useInferenceTasksStore;
